import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileText, Scissors } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (v) => `${Number(v || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

export const ladeTeilstornoPdf = async (rechnungId, t) => {
  try {
    const res = await axios.get(`${API}/rechnungen/${rechnungId}/teilstorno-pdf/${t.id}`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `Stornorechnung_${t.nr}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (err) {
    toast.error("Teilstorno-PDF konnte nicht erstellt werden");
  }
};

export const TeilstornoButton = ({ rechnung, onDone }) => {
  const [offen, setOffen] = useState(false);
  const [betrag, setBetrag] = useState("");
  const [grund, setGrund] = useState("");
  const [busy, setBusy] = useState(false);
  const rest = Number(rechnung.offen_betrag || 0);

  const start = () => { setBetrag(rest.toFixed(2).replace(".", ",")); setGrund(""); setOffen(true); };
  const speichern = async () => {
    const b = parseFloat(String(betrag).replace(/\./g, "").replace(",", "."));
    if (!b || b <= 0) return toast.error("Bitte einen Betrag eingeben");
    if (!grund.trim()) return toast.error("Bitte einen Grund angeben");
    setBusy(true);
    try {
      const { data } = await axios.put(`${API}/rechnungen/${rechnung.id}/teilstorno`, { betrag: b, grund: grund.trim() });
      toast.success(`Teilstorno ${data.storno_rechnungsnummer} erstellt`);
      await ladeTeilstornoPdf(rechnung.id, { id: data.storno_rechnung_id, nr: data.storno_rechnungsnummer });
      setOffen(false);
      onDone && onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Teilstorno fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  if (!offen) {
    return (
      <Button variant="outline" size="sm" className="border-red-300 text-red-600 hover:bg-red-50" onClick={start}
        disabled={rest <= 0} data-testid="teilstorno-open-btn">
        <Scissors size={14} className="mr-1" /> Teilstorno
      </Button>
    );
  }
  return (
    <div className="mt-3 w-full rounded-md border border-red-200 bg-red-50/60 p-3 space-y-2" data-testid="teilstorno-form">
      <div className="text-xs uppercase tracking-wider text-red-700">Teilstorno · offen {eur(rest)}</div>
      <div className="flex gap-2 items-center">
        <span className="text-sm w-20">Betrag €</span>
        <Input value={betrag} onChange={(e) => setBetrag(e.target.value)} className="w-40 bg-white" data-testid="teilstorno-betrag-input" />
      </div>
      <Textarea value={grund} onChange={(e) => setGrund(e.target.value)} rows={2} className="bg-white"
        placeholder="Grund, z. B. Kunde hat die Weiterbildung storniert" data-testid="teilstorno-grund-input" />
      <div className="flex gap-2 justify-end">
        <Button variant="ghost" size="sm" onClick={() => setOffen(false)} data-testid="teilstorno-cancel-btn">Abbrechen</Button>
        <Button variant="destructive" size="sm" onClick={speichern} disabled={busy} data-testid="teilstorno-submit-btn">
          {busy ? "Wird erstellt…" : "Teilstorno buchen"}
        </Button>
      </div>
    </div>
  );
};

export const TeilstornoListe = ({ rechnung }) => {
  const liste = rechnung.teilstornos || [];
  if (!liste.length) return null;
  return (
    <div className="mt-3 p-3 rounded-md border border-red-200 bg-red-50" data-testid="teilstorno-liste">
      <div className="text-xs uppercase tracking-wider text-red-700 mb-1">Teilstornos</div>
      {liste.map((t) => (
        <div key={t.id} className="flex items-center justify-between gap-2 text-sm text-red-900 py-0.5">
          <span>{t.nr} · −{eur(t.betrag)} · {t.grund}</span>
          <button type="button" className="inline-flex items-center gap-1 text-xs underline" onClick={() => ladeTeilstornoPdf(rechnung.id, t)}
            data-testid={`teilstorno-detail-pdf-${t.id}`}>
            <FileText size={12} /> PDF
          </button>
        </div>
      ))}
    </div>
  );
};
